/**
 * 腾讯地图工具类
 * 封装腾讯地图API的常用操作
 */

// 腾讯地图API密钥
const TENCENT_MAP_API_KEY = 'DCHBZ-GXOCO-WQFWB-SVSFB-LSX3E-WJB6A';

// 默认个性化样式ID
const DEFAULT_STYLE_ID = '1';

/**
 * 检查使用的是哪个版本的API
 * @returns {string} 'gljs' 或 'js' 或 null
 */
function detectAPIVersion() {
  // 检查是否是GL JS版本（新版本，使用TMap）
  if (typeof TMap !== 'undefined' && TMap && TMap.Map) {
    return 'gljs';
  }
  // 检查是否是旧版本（使用qq.maps）
  if (typeof qq !== 'undefined' && qq && qq.maps && qq.maps.Map) {
    return 'js';
  }
  return null;
}

/**
 * 等待腾讯地图API加载完成
 * @returns {Promise} Promise对象
 */
export function waitForTencentMapAPI() {
  return new Promise((resolve) => {
    // 优先检查全局标志和API对象
    const version = detectAPIVersion();
    if (window.tencentMapAPILoaded && version) {
      console.log('检测到腾讯地图API版本:', version);
      resolve();
      return;
    }
    
    let checkCount = 0;
    const checkInterval = setInterval(() => {
      checkCount++;
      const currentVersion = detectAPIVersion();
      if (window.tencentMapAPILoaded || currentVersion) {
        clearInterval(checkInterval);
        if (currentVersion) {
          console.log('腾讯地图API加载完成，版本:', currentVersion);
        }
        resolve();
      } else if (checkCount > 100) {
        clearInterval(checkInterval);
        console.error('腾讯地图API加载超时，请检查网络连接和API密钥配置');
        console.log('TMap对象:', typeof TMap, TMap);
        console.log('qq对象:', typeof qq, qq);
        resolve();
      }
    }, 100);
  });
}

/**
 * 检查腾讯地图API是否加载
 * @returns {boolean} 是否已加载
 */
export function isTencentMapAPILoaded() {
  const version = detectAPIVersion();
  return version !== null;
}

/**
 * 创建腾讯地图实例
 * @param {string} containerId - 地图容器ID
 * @param {Object} options - 地图配置选项
 * @param {number} options.centerLng - 中心点经度
 * @param {number} options.centerLat - 中心点纬度
 * @param {number} options.zoom - 缩放级别，默认10
 * @param {string} options.key - API密钥，默认使用全局密钥
 * @param {string} options.mapStyleId - 个性化样式ID，默认'style1'
 * @returns {Object|null} 地图实例
 */
export function createTencentMap(containerId, options = {}) {
  const apiVersion = detectAPIVersion();
  if (!apiVersion) {
    console.error('腾讯地图API未加载，请检查：');
    console.error('1. API脚本是否已加载');
    console.error('2. TMap对象是否存在:', typeof TMap);
    console.error('3. qq对象是否存在:', typeof qq);
    return null;
  }
  
  console.log('使用腾讯地图API版本:', apiVersion);
  
  // 确保 containerId 是字符串类型
  const containerIdStr = String(containerId || '');
  if (!containerIdStr) {
    console.error('地图容器ID不能为空');
    return null;
  }
  
  const mapContainer = document.getElementById(containerIdStr);
  if (!mapContainer) {
    console.error(`地图容器不存在: ${containerIdStr}`);
    console.error('请确保DOM中已存在该ID的元素');
    return null;
  }
  
  // 检查容器是否有尺寸
  const containerRect = mapContainer.getBoundingClientRect();
  if (containerRect.width === 0 || containerRect.height === 0) {
    console.warn('地图容器尺寸为0，可能导致地图不显示');
    console.warn('容器尺寸:', {
      width: containerRect.width,
      height: containerRect.height,
      computedStyle: window.getComputedStyle(mapContainer)
    });
    // 尝试设置最小高度
    if (containerRect.height === 0) {
      mapContainer.style.minHeight = '400px';
      console.warn('已设置容器最小高度为400px');
    }
  }
  
  const {
    centerLng = 113.937122,
    centerLat = 22.542874,
    zoom = 10,
    key = TENCENT_MAP_API_KEY,
    mapStyleId = DEFAULT_STYLE_ID
  } = options;
  
  try {
    // 确保所有参数类型正确
    const keyString = String(key || TENCENT_MAP_API_KEY);
    const lng = Number(centerLng) || 113.937122;
    const lat = Number(centerLat) || 22.542874;
    const zoomLevel = Number(zoom) || 10;
    const styleId = String(mapStyleId || DEFAULT_STYLE_ID);
    
    console.log('创建腾讯地图，参数:', {
      containerId: containerIdStr,
      lng: lng,
      lat: lat,
      zoom: zoomLevel,
      styleId: styleId,
      key: keyString.substring(0, 10) + '...',
      containerSize: {
        width: containerRect.width,
        height: containerRect.height
      }
    });
    
    // 创建腾讯地图实例
    let map = null;
    
    if (apiVersion === 'gljs') {
      // GL JS版本（新版本，使用TMap）
      const mapOptions = {
        center: new TMap.LatLng(lat, lng), // 注意：腾讯地图是纬度在前，经度在后
        zoom: zoomLevel,
        key: keyString // GL JS版本需要在创建时传入key
      };
      
      // GL JS版本的样式设置方式
      // 注意：GL JS版本使用 mapStyleId 参数，且必须是字符串格式（如 'style1'、'style2'）
      if (styleId && styleId !== 'null' && styleId !== 'undefined' && styleId !== '') {
        // 将样式ID转换为字符串格式
        let styleIdStr = String(styleId);
        // 如果样式ID是纯数字，转换为 'style' + 数字 的格式
        const styleIdNum = Number(styleId);
        if (!isNaN(styleIdNum) && styleIdStr === String(styleIdNum)) {
          // 数字格式转换为字符串格式，如 1 -> 'style1'
          styleIdStr = 'style' + styleIdNum;
        }
        // GL JS版本使用 mapStyleId 参数（不是 styleId）
        mapOptions.mapStyleId = styleIdStr;
        console.log('设置个性化样式ID (GL JS):', mapOptions.mapStyleId, '(原始值:', styleId, ')');
      }
      
      console.log('地图配置选项 (GL JS):', mapOptions);
      
      try {
        map = new TMap.Map(mapContainer, mapOptions);
        
        // GL JS版本：确保地图容器可见且已渲染
        // 检查容器是否在DOM中且可见
        const containerStyle = window.getComputedStyle(mapContainer);
        if (containerStyle.display === 'none' || containerStyle.visibility === 'hidden') {
          console.warn('GL JS: 地图容器被隐藏，尝试显示');
          mapContainer.style.display = 'block';
          mapContainer.style.visibility = 'visible';
        }
        
        // 等待一帧后再次检查并resize
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (map && typeof map.resize === 'function') {
              map.resize();
              console.log('GL JS: 地图创建后通过requestAnimationFrame resize');
            }
          });
        });
      } catch (e) {
        console.error('GL JS版本地图创建失败:', e);
        // 如果失败，尝试不传样式ID
        if (mapOptions.mapStyleId) {
          delete mapOptions.mapStyleId;
          console.log('尝试不传样式ID重新创建地图');
          try {
            map = new TMap.Map(mapContainer, mapOptions);
            // 同样等待一帧后resize
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                if (map && typeof map.resize === 'function') {
                  map.resize();
                  console.log('GL JS: 地图创建后通过requestAnimationFrame resize (无样式)');
                }
              });
            });
          } catch (e2) {
            console.error('GL JS版本地图创建失败（无样式）:', e2);
          }
        }
      }
    } else {
      // 旧版本（使用qq.maps）
      const mapOptions = {
        center: new qq.maps.LatLng(lat, lng),
        zoom: zoomLevel
      };
      
      if (styleId && styleId !== 'null' && styleId !== 'undefined' && styleId !== '') {
        mapOptions.mapStyleId = styleId;
        console.log('设置个性化样式ID (旧版):', styleId);
      }
      
      console.log('地图配置选项 (旧版):', mapOptions);
      
      map = new qq.maps.Map(mapContainer, mapOptions);
    }
    
    console.log('腾讯地图创建成功:', map);
    
    // 检查样式是否已应用（仅旧版本支持）
    if (apiVersion === 'js' && map.getMapStyleId) {
      const currentStyleId = map.getMapStyleId();
      console.log('地图当前样式ID:', currentStyleId);
      if (currentStyleId !== styleId) {
        console.warn('样式ID不匹配！期望:', styleId, '实际:', currentStyleId);
      }
    } else if (apiVersion === 'gljs') {
      console.log('GL JS版本样式通过mapStyleId参数设置');
      
      // GL JS版本：如果创建时样式未生效，尝试在地图创建后使用 setMapStyleId 方法
      if (map && styleId && styleId !== 'null' && styleId !== 'undefined' && styleId !== '') {
        setTimeout(() => {
          try {
            let styleIdStr = String(styleId);
            const styleIdNum = Number(styleId);
            if (!isNaN(styleIdNum) && styleIdStr === String(styleIdNum)) {
              // 数字格式转换为字符串格式，如 1 -> 'style1'
              styleIdStr = 'style' + styleIdNum;
            }
            
            if (typeof map.setMapStyleId === 'function') {
              map.setMapStyleId(styleIdStr);
              console.log('GL JS: 已通过setMapStyleId设置样式:', styleIdStr);
              
              // 验证样式是否设置成功
              if (typeof map.getMapStyleId === 'function') {
                const currentStyleId = map.getMapStyleId();
                console.log('GL JS: 当前地图样式ID:', currentStyleId);
                if (currentStyleId !== styleIdStr) {
                  console.warn('⚠️ GL JS样式ID设置可能失败！期望:', styleIdStr, '实际:', currentStyleId);
                  console.warn('可能的原因：');
                  console.warn('1. 样式ID "' + styleIdStr + '" 未在控制台绑定到API密钥');
                  console.warn('2. 样式ID格式不正确（应为 "style1"、"style2" 等字符串格式）');
                  console.warn('3. API密钥没有个性化样式权限');
                  console.warn('4. 一个密钥最多绑定三个样式，请检查是否超出限制');
                  console.warn('5. 请访问 https://lbs.qq.com/dev/console/custom/setting 检查样式绑定状态');
                } else {
                  console.log('✅ GL JS样式ID设置成功:', styleIdStr);
                }
              } else {
                console.warn('GL JS: 地图实例不支持getMapStyleId方法，无法验证样式');
              }
            } else {
              console.warn('GL JS: 地图实例不支持setMapStyleId方法');
            }
          } catch (e) {
            console.error('GL JS: 设置地图样式失败:', e);
          }
        }, 500);
      }
    }
    
    // 如果创建时样式未生效，尝试在地图创建后设置（仅旧版本）
    if (apiVersion === 'js' && map && styleId && styleId !== 'null' && styleId !== 'undefined' && styleId !== '') {
      setTimeout(() => {
        try {
          if (typeof map.setMapStyleId === 'function') {
            const beforeStyleId = map.getMapStyleId ? map.getMapStyleId() : '未知';
            map.setMapStyleId(styleId);
            const afterStyleId = map.getMapStyleId ? map.getMapStyleId() : '未知';
            console.log('已通过setMapStyleId设置样式:', {
              before: beforeStyleId,
              after: afterStyleId,
              target: styleId
            });
            
            if (afterStyleId !== styleId) {
              console.error('⚠️ 样式ID设置失败！可能的原因：');
              console.error('1. 样式ID "' + styleId + '" 未绑定到API密钥');
              console.error('2. 样式ID格式不正确');
              console.error('3. API密钥没有个性化样式权限');
            } else {
              console.log('✅ 样式ID设置成功:', styleId);
            }
          }
        } catch (e) {
          console.error('设置地图样式失败:', e);
        }
      }, 300);
    }
    
    // 确保地图正确初始化
    if (map) {
      // GL JS版本需要等待WebGL上下文初始化
      if (apiVersion === 'gljs') {
        // 使用requestAnimationFrame确保在浏览器重绘前resize
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            try {
              if (typeof map.resize === 'function') {
                map.resize();
                console.log('GL JS: 地图创建后通过requestAnimationFrame resize');
              }
              
              // 检查WebGL上下文
              if (map._glContextLost !== undefined) {
                console.log('GL JS: WebGL上下文状态:', map._glContextLost ? '已丢失' : '正常');
                if (map._glContextLost) {
                  console.warn('GL JS: WebGL上下文已丢失，地图可能无法渲染');
                }
              }
              
              // 检查地图是否真的渲染了
              const canvas = mapContainer.querySelector('canvas');
              if (canvas) {
                const canvasRect = canvas.getBoundingClientRect();
                const canvasStyle = window.getComputedStyle(canvas);
                console.log('GL JS: Canvas尺寸:', { 
                  width: canvasRect.width, 
                  height: canvasRect.height,
                  display: canvasStyle.display,
                  visibility: canvasStyle.visibility,
                  opacity: canvasStyle.opacity,
                  zIndex: canvasStyle.zIndex
                });
                if (canvasRect.width === 0 || canvasRect.height === 0) {
                  console.warn('GL JS: Canvas尺寸为0，地图可能未渲染');
                }
                // 检查Canvas是否被隐藏
                if (canvasStyle.display === 'none' || canvasStyle.visibility === 'hidden' || canvasStyle.opacity === '0') {
                  console.warn('GL JS: Canvas被隐藏:', {
                    display: canvasStyle.display,
                    visibility: canvasStyle.visibility,
                    opacity: canvasStyle.opacity
                  });
                  // 尝试显示Canvas
                  canvas.style.display = 'block';
                  canvas.style.visibility = 'visible';
                  canvas.style.opacity = '1';
                }
              } else {
                console.warn('GL JS: 未找到Canvas元素，地图可能未渲染');
                // 等待一段时间后再次检查
                setTimeout(() => {
                  const retryCanvas = mapContainer.querySelector('canvas');
                  if (retryCanvas) {
                    console.log('GL JS: 延迟找到Canvas元素');
                    const retryRect = retryCanvas.getBoundingClientRect();
                    console.log('GL JS: Canvas尺寸（延迟检查）:', { width: retryRect.width, height: retryRect.height });
                  } else {
                    console.error('GL JS: 延迟检查仍未找到Canvas元素，地图可能未正确创建');
                  }
                }, 1000);
              }
              
              // 再次检查容器尺寸并resize
              const mapRect = mapContainer.getBoundingClientRect();
              if (mapRect.width > 0 && mapRect.height > 0) {
                // 容器有尺寸，延迟再次resize确保渲染
                setTimeout(() => {
                  if (typeof map.resize === 'function') {
                    map.resize();
                    console.log('GL JS: 地图创建后延迟resize（确保渲染）');
                  }
                }, 300);
              } else {
                console.warn('GL JS: 地图创建后容器尺寸仍为0');
              }
            } catch (e) {
              console.warn('GL JS: 地图创建后resize失败:', e);
            }
          });
        });
      } else {
        // 旧版本延迟检查
        setTimeout(() => {
          const mapRect = mapContainer.getBoundingClientRect();
          if (mapRect.width === 0 || mapRect.height === 0) {
            console.warn('地图创建后容器尺寸仍为0，请检查CSS样式');
          }
        }, 500);
      }
    }
    
    return map;
  } catch (error) {
    console.error('创建腾讯地图失败:', error);
    console.error('错误详情:', error.message);
    if (error.stack) {
      console.error('堆栈:', error.stack);
    }
    return null;
  }
}

/**
 * 设置地图样式
 * @param {Object} map - 地图实例
 * @param {string} styleId - 个性化样式ID，默认'style1'
 */
export function setMapStyle(map, styleId = DEFAULT_STYLE_ID) {
  if (!map || !isTencentMapAPILoaded()) {
    console.error('地图实例不存在或腾讯地图API未加载');
    return;
  }
  
  try {
    const styleIdStr = String(styleId || DEFAULT_STYLE_ID);
    if (typeof map.setMapStyleId === 'function') {
      map.setMapStyleId(styleIdStr);
    } else {
      console.warn('地图实例不支持setMapStyleId方法');
    }
  } catch (error) {
    console.error('设置地图样式失败:', error);
  }
}

/**
 * 设置地图事件（启用滚轮缩放、拖拽等）
 * @param {Object} map - 地图实例
 */
export function setMapEvent(map) {
  if (!map) return;
  
  const apiVersion = detectAPIVersion();
  
  try {
    if (apiVersion === 'gljs') {
      // GL JS版本默认已启用滚轮缩放和拖拽
      console.log('GL JS版本地图事件已默认启用');
    } else {
      // 旧版本
      if (typeof map.enableScrollWheelZoom === 'function') {
        map.enableScrollWheelZoom();
      }
      if (typeof map.enableDragging === 'function') {
        map.enableDragging();
      }
    }
  } catch (error) {
    console.warn('设置地图事件失败:', error);
  }
}

/**
 * 创建标记点
 * @param {number} lng - 经度
 * @param {number} lat - 纬度
 * @param {Object} options - 标记选项
 * @param {string} options.iconUrl - 图标URL
 * @param {Object} options.iconSize - 图标大小 {width, height}
 * @param {Object} options.iconAnchor - 图标锚点 {x, y}
 * @returns {Object|null} 标记点实例
 */
export function createMarker(lng, lat, options = {}) {
  const apiVersion = detectAPIVersion();
  if (!apiVersion) {
    console.error('腾讯地图API未加载');
    return null;
  }
  
  const {
    iconUrl = null,
    iconSize = { width: 26, height: 36 },
    iconAnchor = { x: 13, y: 36 }
  } = options;
  
  try {
    if (apiVersion === 'gljs') {
      // GL JS版本
      const position = new TMap.LatLng(lat, lng);
      const markerOptions = {
        position: position
      };
      
      if (iconUrl) {
        markerOptions.icon = iconUrl;
        markerOptions.width = iconSize.width;
        markerOptions.height = iconSize.height;
      }
      
      const marker = new TMap.MultiMarker({
        map: null, // 稍后通过addMarkerToMap添加
        styles: iconUrl ? {
          default: new TMap.MarkerStyle({
            width: iconSize.width,
            height: iconSize.height,
            anchor: { x: iconAnchor.x, y: iconAnchor.y },
            src: iconUrl
          })
        } : undefined,
        geometries: [{
          id: 'marker_' + Date.now() + '_' + Math.random(),
          styleId: 'default',
          position: position
        }]
      });
      
      return marker;
    } else {
      // 旧版本
      const position = new qq.maps.LatLng(lat, lng);
      const markerOptions = {
        position: position
      };
      
      if (iconUrl) {
        markerOptions.icon = new qq.maps.MarkerImage(
          iconUrl,
          new qq.maps.Size(iconSize.width, iconSize.height),
          new qq.maps.Point(iconAnchor.x, iconAnchor.y),
          new qq.maps.Point(iconSize.width / 2, iconSize.height)
        );
      }
      
      const marker = new qq.maps.Marker(markerOptions);
      return marker;
    }
  } catch (error) {
    console.error('创建标记点失败:', error);
    return null;
  }
}

/**
 * 添加标记点到地图
 * @param {Object} map - 地图实例
 * @param {Object} marker - 标记点实例
 * @returns {boolean} 是否添加成功
 */
export function addMarkerToMap(map, marker) {
  if (!map || !marker) return false;
  
  const apiVersion = detectAPIVersion();
  
  try {
    if (apiVersion === 'gljs') {
      // GL JS版本
      if (marker.setMap) {
        marker.setMap(map);
      } else if (marker.map === null || marker.map === undefined) {
        marker.map = map;
      }
    } else {
      // 旧版本
      marker.setMap(map);
    }
    return true;
  } catch (error) {
    console.error('添加标记点到地图失败:', error);
    return false;
  }
}

/**
 * 移除标记点
 * @param {Object} marker - 标记点实例
 * @returns {boolean} 是否移除成功
 */
export function removeMarkerFromMap(marker) {
  if (!marker) return false;
  
  try {
    marker.setMap(null);
    return true;
  } catch (error) {
    console.error('移除标记点失败:', error);
    return false;
  }
}

/**
 * 清除所有标记点
 * @param {Array} markers - 标记点数组
 */
export function clearAllMarkers(markers) {
  if (!markers || !Array.isArray(markers)) return;
  
  markers.forEach(marker => {
    try {
      if (marker && typeof marker.setMap === 'function') {
        marker.setMap(null);
      }
    } catch (error) {
      console.warn('清除标记点失败:', error);
    }
  });
}

/**
 * 设置地图中心点和缩放级别
 * @param {Object} map - 地图实例
 * @param {number} lng - 经度
 * @param {number} lat - 纬度
 * @param {number} zoom - 缩放级别（可选）
 */
export function setMapCenter(map, lng, lat, zoom) {
  if (!map || !isTencentMapAPILoaded()) return;
  
  const apiVersion = detectAPIVersion();
  
  try {
    if (apiVersion === 'gljs') {
      const center = new TMap.LatLng(lat, lng);
      if (zoom !== undefined) {
        map.setCenter(center);
        map.setZoom(zoom);
      } else {
        map.setCenter(center);
      }
    } else {
      const center = new qq.maps.LatLng(lat, lng);
      if (zoom !== undefined) {
        map.setCenter(center);
        map.setZoom(zoom);
      } else {
        map.setCenter(center);
      }
    }
  } catch (error) {
    console.error('设置地图中心点失败:', error);
  }
}

/**
 * 调整地图尺寸（当容器尺寸变化时调用）
 * @param {Object} map - 地图实例
 */
export function resizeMap(map) {
  if (!map) {
    console.warn('resizeMap: 地图实例不存在');
    return;
  }
  
  const apiVersion = detectAPIVersion();
  
  try {
    // 获取地图容器
    let mapContainer = null;
    
    // 尝试多种方式获取容器
    if (map.getContainer && typeof map.getContainer === 'function') {
      mapContainer = map.getContainer();
    } else if (map.container) {
      mapContainer = map.container;
    } else {
      // 尝试通过地图实例的DOM元素获取
      const mapElement = document.getElementById('map');
      if (mapElement) {
        mapContainer = mapElement;
      }
    }
    
    if (mapContainer) {
      // 先检查容器是否被隐藏
      const computedStyle = window.getComputedStyle(mapContainer);
      const isHidden = computedStyle.display === 'none' || 
                       computedStyle.visibility === 'hidden';
      
      if (isHidden) {
        console.warn('地图容器被隐藏:', {
          display: computedStyle.display,
          visibility: computedStyle.visibility
        });
        // 尝试显示容器
        mapContainer.style.display = 'block';
        mapContainer.style.visibility = 'visible';
      }
      
      const rect = mapContainer.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;
      
      console.log('地图容器尺寸:', { 
        width, 
        height,
        computedHeight: computedStyle.height,
        computedWidth: computedStyle.width,
        parentHeight: mapContainer.parentElement ? mapContainer.parentElement.getBoundingClientRect().height : '无父容器'
      });
      
      // 如果容器尺寸为0，尝试设置最小尺寸（不重试）
      if (width === 0 || height === 0) {
        console.warn('地图容器尺寸为0，尝试设置最小尺寸');
        
        // 尝试多种方式设置容器尺寸
        if (height === 0) {
          // 检查父容器链，找到第一个有高度的父容器
          let parent = mapContainer.parentElement;
          let foundHeight = false;
          let parentLevel = 0;
          
          while (parent && parentLevel < 5) {
            const parentRect = parent.getBoundingClientRect();
            const computedStyle = window.getComputedStyle(parent);
            
            if (parentRect.height > 0) {
              // 找到有高度的父容器，使用它的高度
              mapContainer.style.height = parentRect.height + 'px';
              mapContainer.style.minHeight = parentRect.height + 'px';
              console.log(`使用第${parentLevel}层父容器高度:`, parentRect.height);
              foundHeight = true;
              break;
            }
            
            parent = parent.parentElement;
            parentLevel++;
          }
          
          // 如果所有父容器都没有高度，设置固定高度
          if (!foundHeight) {
            mapContainer.style.minHeight = '400px';
            mapContainer.style.height = '400px';
            mapContainer.style.flex = '1';
            mapContainer.style.display = 'block';
            console.log('所有父容器都没有高度，设置固定高度400px');
          }
        }
        if (width === 0) {
          mapContainer.style.width = '100%';
          mapContainer.style.minWidth = '400px';
          console.log('已设置容器宽度');
        }
        
        // 确保容器可见
        const computedStyle = window.getComputedStyle(mapContainer);
        if (computedStyle.display === 'none') {
          mapContainer.style.display = 'block';
          console.log('容器被隐藏，已设置为display: block');
        }
        if (computedStyle.visibility === 'hidden') {
          mapContainer.style.visibility = 'visible';
          console.log('容器被隐藏，已设置为visibility: visible');
        }
        
        // 设置尺寸后，直接尝试resize，不再重试
        console.log('容器尺寸已设置，直接执行resize');
      }
    } else {
      console.warn('无法获取地图容器，尝试直接resize');
      // 尝试通过ID直接获取
      const mapElement = document.getElementById('map');
      if (mapElement) {
        console.log('通过ID找到地图容器，强制设置尺寸');
        mapElement.style.minHeight = '400px';
        mapElement.style.height = '400px';
        mapElement.style.width = '100%';
      }
    }
    
    if (apiVersion === 'gljs') {
      // GL JS版本 - 尝试多种方法，确保地图重新渲染
      // 方法1: 尝试resize方法（最常用）
      if (typeof map.resize === 'function') {
        try {
          map.resize();
          console.log('GL JS: 使用resize()方法');
        } catch (e) {
          console.warn('GL JS: resize()方法失败:', e);
        }
      }
      
      // 方法2: 尝试updateSize方法
      if (typeof map.updateSize === 'function') {
        try {
          map.updateSize();
          console.log('GL JS: 使用updateSize()方法');
        } catch (e) {
          console.warn('GL JS: updateSize()方法失败:', e);
        }
      }
      
      // 方法3: 尝试invalidateSize方法
      if (typeof map.invalidateSize === 'function') {
        try {
          map.invalidateSize();
          console.log('GL JS: 使用invalidateSize()方法');
        } catch (e) {
          console.warn('GL JS: invalidateSize()方法失败:', e);
        }
      }
      
      // 方法4: 强制重新渲染 - 通过重新设置中心点和缩放级别
      // 这个方法可以强制地图重新计算和渲染
      try {
        if (map.getCenter && map.getZoom && map.setCenter && map.setZoom) {
          const currentCenter = map.getCenter();
          const currentZoom = map.getZoom();
          
          if (currentCenter && currentZoom !== undefined) {
            // 先稍微改变缩放级别，再改回来，强制重新渲染
            const tempZoom = currentZoom + 0.0001;
            map.setZoom(tempZoom);
            
            setTimeout(() => {
              map.setZoom(currentZoom);
              if (currentCenter.lat && currentCenter.lng) {
                map.setCenter(new TMap.LatLng(currentCenter.lat, currentCenter.lng));
              } else if (currentCenter.getLat && currentCenter.getLng) {
                map.setCenter(new TMap.LatLng(currentCenter.getLat(), currentCenter.getLng()));
              }
              console.log('GL JS: 通过重新设置缩放级别强制重新渲染');
              
              // 再次调用resize确保渲染
              setTimeout(() => {
                if (typeof map.resize === 'function') {
                  map.resize();
                  console.log('GL JS: 强制重新渲染后再次resize');
                }
              }, 100);
            }, 200);
          }
        }
      } catch (e) {
        console.warn('GL JS: 强制重新渲染失败:', e);
      }
      
      // 方法5: 不触发窗口resize事件，避免循环调用
      // 注释掉，因为会触发window的resize监听器，导致无限循环
      // if (window.dispatchEvent) {
      //   try {
      //     const resizeEvent1 = new Event('resize', { bubbles: true });
      //     window.dispatchEvent(resizeEvent1);
      //     if (mapContainer) {
      //       const resizeEvent2 = new Event('resize', { bubbles: true });
      //       mapContainer.dispatchEvent(resizeEvent2);
      //     }
      //     console.log('GL JS: 触发窗口和容器resize事件');
      //   } catch (e) {
      //     console.warn('GL JS: 触发resize事件失败:', e);
      //   }
      // }
      
      // 方法6: 如果地图有render方法，直接调用
      if (typeof map.render === 'function') {
        try {
          map.render();
          console.log('GL JS: 使用render()方法');
        } catch (e) {
          console.warn('GL JS: render()方法失败:', e);
        }
      }
      
      // 方法7: 如果地图有refresh方法，调用它
      if (typeof map.refresh === 'function') {
        try {
          map.refresh();
          console.log('GL JS: 使用refresh()方法');
        } catch (e) {
          console.warn('GL JS: refresh()方法失败:', e);
        }
      }
    } else {
      // 旧版本
      if (typeof map.checkResize === 'function') {
        map.checkResize();
        console.log('旧版: 使用checkResize()方法');
      } else if (typeof map.resize === 'function') {
        map.resize();
        console.log('旧版: 使用resize()方法');
      } else if (typeof map.updateSize === 'function') {
        map.updateSize();
        console.log('旧版: 使用updateSize()方法');
      } else {
        // 不触发窗口resize事件，避免循环调用
        // 注释掉，因为会触发window的resize监听器，导致无限循环
        // if (window.dispatchEvent) {
        //   const resizeEvent = new Event('resize');
        //   window.dispatchEvent(resizeEvent);
        //   console.log('旧版: 触发窗口resize事件');
        // }
      }
    }
    console.log('✅ 地图尺寸已调整');
  } catch (error) {
    console.error('❌ 调整地图尺寸失败:', error);
    console.error('错误详情:', error.message, error.stack);
  }
}

//清除marker
export function clearMarker(markers){
    if(markers.length){
        for (let i in markers) {
           markers[i].setMap(null);
       }
   }
}

//添加marker
export function addMarker(lat,lng,map){
    var center = new qq.maps.LatLng(lat,lng);
    var marker= new qq.maps.Marker({
            position: center,
            map:map
        });

    return marker;
}

//点聚合
export function markerCluster(map,markers){
   new qq.maps.MarkerCluster({
    map:map,
    minimumClusterSize:2, //默认2
    markers:markers,
    zoomOnClick:true, //默认为true
    gridSize:30, //默认60
    averageCenter:true, //默认false
    maxZoom:18, //默认18
   })
}

//信息窗
export function informationWindow(map,marker,c){
    var name=c.parkingName;
    var address=c.parkingAddress;
    var content = "<h1 style='width:100%;backgruond-color:#ccc;height:30px;font-size:20px;text-align:center;'>" + name + "</h1><ul><li style='border-bottom:solid 1px #ccc;height:30px;line-height:30px;'>地址:" +address + "</li>" +
            "</ul>";
    //初始化infoWindow
    console.log(c)
    var infoWindow = new qq.maps.InfoWindow({
        map: map,
        position: marker,
        offset: { x: -8, y: -32 } //设置信息窗相对position偏移像素
    });
    infoWindow.close();//初始关闭信息窗关闭
    //监听标注点击事件
    qq.maps.event.addListener(marker,'click', function() {
        infoWindow.open();
        infoWindow.setContent(content);
        
    });
}

/**
 * 为腾讯地图创建标记点信息窗口（兼容createTag函数）
 * @param {Object} map - 地图实例
 * @param {Object} marker - 标记点实例
 * @param {Object} rows - 数据对象，包含parkingName、parkingAddress、pointLng、pointLat等
 */
export function createTagForTencentMap(map, marker, rows) {
  if (!map || !marker || !rows || !isTencentMapAPILoaded()) {
    console.error('参数不完整或腾讯地图API未加载');
    return;
  }
  
  const apiVersion = detectAPIVersion();
  if (!apiVersion) {
    console.error('无法检测腾讯地图API版本');
    return;
  }
  
  try {
    const content = "<h1 style='width:100%;height:30px;border-radius:10px;line-height:30px;font-size:20px;text-align:center;color:#00CC33'>" + 
      (rows.parkingName || '') + "</h1><ul style='border-radius:10px'><li style='border-bottom:solid 1px #ccc;height:30px;line-height:30px;color:#666;font-weight:bold'>地址:" + 
      (rows.parkingAddress || '') + "</li></ul>";
    
    // 获取标记点位置
    const markerLat = rows.pointLat || 0;
    const markerLng = rows.pointLng || 0;
    
    if (apiVersion === 'gljs') {
      // GL JS版本
      const markerPosition = new TMap.LatLng(markerLat, markerLng);
      
      // GL JS版本创建信息窗口
      const infoWindow = new TMap.InfoWindow({
        map: map,
        position: markerPosition,
        content: content
      });
      
      // 初始关闭信息窗口
      infoWindow.close();
      
      // 当前打开的信息窗口引用
      let currentInfoWindow = null;
      
      // GL JS版本的事件监听方式
      // 注意：GL JS版本的MultiMarker可能需要通过geometry的id来监听事件
      if (marker.on) {
        // 使用on方法监听事件
        marker.on('click', function(evt) {
          // 如果已有信息窗口打开，先关闭
          if (currentInfoWindow && currentInfoWindow !== infoWindow) {
            currentInfoWindow.close();
          }
          
          // 如果点击的是同一个marker，切换显示/隐藏
          if (currentInfoWindow === infoWindow) {
            infoWindow.close();
            currentInfoWindow = null;
          } else {
            // 设置内容并打开信息窗口
            infoWindow.setContent(content);
            infoWindow.open();
            currentInfoWindow = infoWindow;
          }
        });
      } else {
        // 如果marker不支持on方法，尝试通过地图监听点击事件
        map.on('click', function(evt) {
          // 检查点击位置是否在标记点附近
          const clickLat = evt.latLng.lat;
          const clickLng = evt.latLng.lng;
          const distance = Math.sqrt(
            Math.pow(clickLat - markerLat, 2) + Math.pow(clickLng - markerLng, 2)
          );
          
          // 如果点击位置在标记点附近（0.001度范围内），显示信息窗口
          if (distance < 0.001) {
            if (currentInfoWindow && currentInfoWindow !== infoWindow) {
              currentInfoWindow.close();
            }
            
            if (currentInfoWindow === infoWindow) {
              infoWindow.close();
              currentInfoWindow = null;
            } else {
              infoWindow.setContent(content);
              infoWindow.open();
              currentInfoWindow = infoWindow;
            }
          }
        });
      }
    } else {
      // 旧版本（使用qq.maps）
      const markerPosition = new qq.maps.LatLng(markerLat, markerLng);
      
      // 创建信息窗口
      const infoWindow = new qq.maps.InfoWindow({
        map: map,
        position: markerPosition,
        offset: { x: -8, y: -32 }
      });
      
      // 初始关闭信息窗口
      infoWindow.close();
      
      // 当前打开的信息窗口引用
      let currentInfoWindow = null;
      
      // 监听标记点点击事件
      qq.maps.event.addListener(marker, 'click', function() {
        // 如果已有信息窗口打开，先关闭
        if (currentInfoWindow && currentInfoWindow !== infoWindow) {
          currentInfoWindow.close();
        }
        
        // 如果点击的是同一个marker，切换显示/隐藏
        if (currentInfoWindow === infoWindow) {
          infoWindow.close();
          currentInfoWindow = null;
        } else {
          // 设置内容并打开信息窗口
          infoWindow.setContent(content);
          infoWindow.open();
          currentInfoWindow = infoWindow;
        }
      });
    }
  } catch (error) {
    console.error('创建腾讯地图信息窗口失败:', error);
    console.error('错误详情:', error.message);
    console.error('API版本:', apiVersion);
  }
}

export default {
  waitForTencentMapAPI,
  isTencentMapAPILoaded,
  createTencentMap,
  setMapStyle,
  setMapEvent,
  createMarker,
  addMarkerToMap,
  removeMarkerFromMap,
  clearAllMarkers,
  setMapCenter,
  resizeMap,
  clearMarker,
  addMarker,
  markerCluster,
  informationWindow,
  createTagForTencentMap,
  TENCENT_MAP_API_KEY,
  DEFAULT_STYLE_ID
};
